import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useLocation,
} from "react-router-dom";
import useAppDispatch from "./hooks/useAppDispatch";

import { DefaultLayout, AuthLayout } from "./layouts";
import { Preloader } from "./components";
import ScrollToTop from "./helpers/scroll-top";
import "./helpers/i18n";

import { refreshToken } from "./store/action/user";
import jwt_decode from "jwt-decode";
import { getCookie } from "./helpers/cookies";
import { setAxiosClientInterceptor } from "./helpers/apiClient";
// import PrivateRoute from "./components/PrivateRoute";

const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const ForgetPassword = lazy(() => import("./pages/ForgetPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Home = lazy(() => import("./pages/Home"));
const AllProducts = lazy(() => import("./pages/AllProducts"));
const ProductMaintenance = lazy(() => import("./pages/ProductMaintenance"));
const ProductMBIZ = lazy(() => import("./pages/ProductMBiz"));
// const Chat = lazy(() => import("./pages/Chat"));
const Community = lazy(() => import("./pages/Community"));
const Cart = lazy(() => import("./pages/Cart"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const CheckoutMaintenance = lazy(() => import("./pages/CheckoutMaintenance"));
const CheckoutMbiz = lazy(() => import("./pages/CheckoutMbiz"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Search = lazy(() => import("./pages/Search"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Notification = lazy(() => import("./pages/Notification"));
// const Contact = lazy(() => import("./pages/Contact"));
const Order = lazy(() => import("./pages/Order"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const AnnoucementList = lazy(() => import("./pages/AnnoucementList"));
const Annoucement = lazy(() => import("./pages/Annoucement"));
const Policy = lazy(() => import("./pages/Policy"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const RefundAndReturn = lazy(() => import("./pages/Policy/RefundAndReturn"));
const Privacy = lazy(() => import("./pages/Policy/Privacy"));
const MerchantService = lazy(() => import("./pages/Policy/MerchantService"));
const TermOfUse = lazy(() => import("./pages/Policy/TermOfUse"));
const EditBankInformation = lazy(() => import("./pages/EditBankInformation"));
const Wallet = lazy(() => import("./pages/Wallet"));
const WalletDetails = lazy(() => import("./pages/Wallet/WalletDetail"));
const WalletConvert = lazy(() => import("./pages/Wallet/WalletConvert"));
const TransactionHistory = lazy(
  () => import("./pages/Wallet/TransactionHistory"),
);
const WithdrawalHistory = lazy(
  () => import("./pages/Wallet/WithdrawalHistory"),
);
const WalletWithdrawal = lazy(() => import("./pages/Wallet/WalletWithdrawal"));
const TransferWallet = lazy(() => import("./pages/Wallet/WalletTransfer"));
const BonusDetails = lazy(() => import("./pages/Wallet/Bonus/BonusDetail"));
const BonusTransactionHistory = lazy(
  () => import("./pages/Wallet/Bonus/BonusTransactionHistory"),
);
const Resource = lazy(() => import("./pages/Resource"));
const ResourceFile = lazy(() => import("./pages/Resource/File"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const ChangeSecurityPassword = lazy(
  () => import("./pages/ChangeSecurityPassword"),
);

const Address = lazy(() => import("./pages/Address"));
const AddressCreate = lazy(() => import("./pages/Address/Create"));
const AddressEdit = lazy(() => import("./pages/Address/Edit"));

function App() {
  const dispatch = useAppDispatch();

  const refreshTkn = async () => {
    const response = await dispatch(refreshToken()).unwrap();
    if (response.isAuth) {
      setAxiosClientInterceptor();
    }
  };

  const validateToken = async () => {
    const authToken = getCookie("authToken");
    if (authToken) {
      //check expiry
      type authContent = {
        unique_name: string;
        nbf: number;
        exp: number;
        iat: number;
      };
      const content: authContent = jwt_decode(authToken);

      const nowDate = new Date();
      if (nowDate.getTime() > content.exp) {
        refreshTkn();
      } else {
        setAxiosClientInterceptor();
      }

      return;
    }

    refreshTkn();
  };

  useEffect(() => {
    validateToken();
  }, []);

  return (
    <Router>
      <ScrollToTop>
        <Suspense fallback={<Preloader />}>
          <Routes>
            <Route
              element={
                <DefaultLayout>
                  <Outlet />
                </DefaultLayout>
              }
            >
              <Route
                path={process.env.PUBLIC_URL + "/login"}
                element={<Login />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/forgetPassword"}
                element={<ForgetPassword />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/resetPassword"}
                element={<ResetPassword />}
              />
              <Route path={process.env.PUBLIC_URL + "/"} element={<Home />} />
              <Route
                path={process.env.PUBLIC_URL + "/home"}
                element={<Home />}
              />

              <Route
                path={process.env.PUBLIC_URL + "/products/:productTypeParam"}
                element={<AllProducts />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/product/maintenance/:id"}
                element={<ProductMaintenance />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/product/mbiz/:id"}
                element={<ProductMBIZ />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/policy"}
                element={<Policy />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/policy/refundAndReturn"}
                element={<RefundAndReturn />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/policy/privacy"}
                element={<Privacy />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/policy/merchantService"}
                element={<MerchantService />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/policy/termOfUse"}
                element={<TermOfUse />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/contactUs"}
                element={<ContactUs />}
              />

              <Route element={<AuthLayout />}>
                <Route
                  path={process.env.PUBLIC_URL + "/register"}
                  element={<Register />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/annoucement"}
                  element={<AnnoucementList />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/annoucement/:annoucementId"}
                  element={<Annoucement />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/community"}
                  element={<Community />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/cart"}
                  element={<Cart />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/wishlist"}
                  element={<Wishlist />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/checkout/maintenance"}
                  element={<CheckoutMaintenance />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/checkout/mbiz/:productId"}
                  element={<CheckoutMbiz />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/search"}
                  element={<Search />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/profile"}
                  element={<Profile />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/edit-profile"}
                  element={<EditProfile />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/address"}
                  element={<Address />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/address/create"}
                  element={<AddressCreate />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/address/edit/:id"}
                  element={<AddressEdit />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/notification"}
                  element={<Notification />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/resource"}
                  element={<Resource />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/resource/:id"}
                  element={<ResourceFile />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/order"}
                  element={<Order />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/order/:orderNo"}
                  element={<OrderDetail />}
                />

                <Route
                  path={process.env.PUBLIC_URL + "/editBankInformation"}
                  element={<EditBankInformation />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/wallet"}
                  element={<Wallet />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/wallet/:walletType"}
                  element={<WalletDetails />}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/wallet/convert/:walletType"}
                  element={<WalletConvert />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/wallet/transaction/:walletType`}
                  element={<TransactionHistory />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/wallet/withdrawalHistory/:walletType`}
                  element={<WithdrawalHistory />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/wallet/withdrawal/:walletType`}
                  element={<WalletWithdrawal />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/wallet/transfer/:walletType`}
                  element={<TransferWallet />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/wallet/bonus/`}
                  element={<BonusDetails />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/wallet/bonus/:bonusType`}
                  element={<BonusTransactionHistory />}
                />

                <Route
                  path={`${process.env.PUBLIC_URL}/changePassword`}
                  element={<ChangePassword />}
                />

                <Route
                  path={`${process.env.PUBLIC_URL}/changeSecurityPassword`}
                  element={<ChangeSecurityPassword />}
                />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </ScrollToTop>
    </Router>
  );
}

export default App;
